import { supabase } from '@/integrations/supabase/client';

export interface PortfolioAnalysisRequest {
  imageUrls: string[];
  userProfession?: string;
  userSkills?: string[];
}

export interface PortfolioAnalysisResponse {
  title: string;
  description: string;
  confidence: number;
  detectedElements: string[];
  analyzedImageCount: number;
  totalImageCount: number;
}

export interface PortfolioAnalysisError {
  error: string;
  details?: string;
  event_id?: string;
}

/**
 * Analyze portfolio images using AI to generate project title and description
 * Uses intelligent image selection to analyze only 2-3 images for cost efficiency
 * Focuses on analyzing the actual images rather than user skills
 */
export async function analyzePortfolioImages(
  request: PortfolioAnalysisRequest
): Promise<PortfolioAnalysisResponse> {
  try {
    const { data, error } = await supabase.functions.invoke('analyze-portfolio-images', {
      body: {
        imageUrls: request.imageUrls,
        userProfession: request.userProfession || 'designer',
        userSkills: [] // Don't pass user skills to focus on image analysis
      }
    });

    if (error) {
      throw new Error(`Supabase function error: ${error.message}`);
    }

    if (!data || !data.success) {
      throw new Error(data?.error || 'Unknown error from analysis');
    }

    // Calculate which images were analyzed
    const totalImageCount = request.imageUrls.length;
    const analyzedImageCount = Math.min(totalImageCount, 3); // AI analyzes max 3 images

    return {
      ...data.data,
      analyzedImageCount,
      totalImageCount
    };

  } catch (error) {
    console.error('Portfolio analysis failed:', error);
    
    // Return a fallback response with error information
    return {
      title: "Portfolio Project",
      description: "A creative design project showcasing professional skills and expertise.",
      confidence: 0.5,
      detectedElements: ["design", "portfolio", "project"],
      analyzedImageCount: Math.min(request.imageUrls.length, 3),
      totalImageCount: request.imageUrls.length
    };
  }
}

/**
 * Get a user-friendly message about the AI analysis process
 */
export function getAnalysisStatusMessage(
  analyzedCount: number,
  totalCount: number,
  isAnalyzing: boolean = false
): string {
  if (isAnalyzing) {
    return `Analyzing ${analyzedCount} of ${totalCount} images...`;
  }
  
  if (totalCount > analyzedCount) {
    return `We have analyzed ${analyzedCount} of ${totalCount} images for optimal results`;
  }
  
  return `We have analyzed all ${totalCount} images`;
}

/**
 * Get cost optimization message for users
 */
export function getCostOptimizationMessage(totalCount: number): string {
  if (totalCount <= 3) {
    return "All images will be analyzed";
  }
  
  return `For cost efficiency, we will analyze 3 of ${totalCount} images. You can still upload all ${totalCount} images to your portfolio.`;
} 